import { type DriveDocumentListItem, DriveDocumentSearch } from '@platform/documents';
import { FEATURE_FLAGS, useFeatureFlag } from '@platform/feature-flags';
import { Alert, Box, Button, Chip, Divider, Icon, IconButton, Paper, Typography } from '@shared/ui';
import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { analytics } from '../../services/analytics';
import { useAppSelector } from '../../store/hooks';
import { PROFILE_SLUGS, profileSlugToArtifactType } from '../coach-artifacts/utils/profileSlugs';

const GENERATION_ACTIONS = [
  {
    label: 'Coaching Conversation Summary',
    maxDocuments: 1,
    minDocuments: 1,
    profile: PROFILE_SLUGS.COACHING_CONVERSATION_SUMMARY,
  },
  {
    label: 'Coaching Conversations Summary',
    minDocuments: 2,
    profile: PROFILE_SLUGS.COACHING_CONVERSATIONS_SUMMARY,
  },
  {
    label: 'Coaching Feedback',
    maxDocuments: 1,
    minDocuments: 1,
    profile: PROFILE_SLUGS.COACHING_FEEDBACK,
  },
  {
    label: 'Coaching Feedback Arc',
    minDocuments: 2,
    profile: PROFILE_SLUGS.COACHING_FEEDBACK_ARC,
  },
] as const;

type GenerationAction = (typeof GENERATION_ACTIONS)[number];

function upsertSelectedDocuments(
  current: DriveDocumentListItem[],
  incoming: DriveDocumentListItem[]
): { docs: DriveDocumentListItem[]; message: string | null } {
  let next = current;
  let message: string | null = null;

  for (const candidate of incoming) {
    if (next.some((doc) => doc.documentId === candidate.documentId)) {
      message = 'That document is already selected.';
      continue;
    }
    next = [...next, candidate];
    message = null;
  }

  return { docs: next, message };
}

function canGenerateAction(action: GenerationAction, selectedCount: number): boolean {
  if (selectedCount < action.minDocuments) {
    return false;
  }
  if ('maxDocuments' in action && selectedCount > action.maxDocuments) {
    return false;
  }
  return true;
}

const ManualTranscriptsPage: React.FC = () => {
  const navigate = useNavigate();
  const personId = useAppSelector((state) => state.auth.user?.id) || '';
  const isEnabled = useFeatureFlag(FEATURE_FLAGS.MANUAL_DRIVE_ARTIFACT_GENERATION);
  const [selectedDocs, setSelectedDocs] = React.useState<DriveDocumentListItem[]>([]);
  const [selectionMessage, setSelectionMessage] = React.useState<string | null>(null);
  const [searchError, setSearchError] = React.useState<string | null>(null);

  if (!isEnabled) {
    return <Navigate replace to="/no-access" />;
  }

  if (personId.trim() === '') {
    return <Navigate replace to="/no-access" />;
  }

  const handleDocumentsSelected = (docs: DriveDocumentListItem[]) => {
    const next = upsertSelectedDocuments(selectedDocs, docs);
    setSelectedDocs(next.docs);
    setSelectionMessage(next.message);
  };

  const handleRemoveDocument = (documentId: string) => {
    setSelectedDocs((current) => current.filter((doc) => doc.documentId !== documentId));
    setSelectionMessage(null);
  };

  const handleGenerate = (action: GenerationAction) => {
    if (!canGenerateAction(action, selectedDocs.length)) {
      return;
    }

    analytics.track('Clicked generate artifact', {
      profile_slug: action.profile,
      artifact_type: profileSlugToArtifactType(action.profile) ?? undefined,
      source_count: selectedDocs.length,
      source_kind: 'google_drive_manual_selection',
    });

    const params = new URLSearchParams();
    params.set('docs', selectedDocs.map((doc) => doc.documentId).join(','));
    params.set('profile', action.profile);
    setSelectedDocs([]);
    setSelectionMessage(null);
    setSearchError(null);
    navigate(`/drive1on1/summary?${params.toString()}`);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'sunny.100',
        width: '100%',
      }}
    >
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: 3, py: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h4"
            sx={{
              fontFamily: 'Basis Grotesque',
              fontWeight: 700,
              fontSize: '32px',
              lineHeight: 1.2,
              color: 'cement.500',
            }}
          >
            Manual Transcripts
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            Search your Google Drive, select documents, then generate one of the approved artifact
            types.
          </Typography>
        </Box>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Search Google Drive
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This workflow only uses the documents you explicitly select. It does not rely on member
            or session context.
          </Typography>
          <DriveDocumentSearch
            personId={personId}
            onDocumentsSelected={handleDocumentsSelected}
            onErrorChange={setSearchError}
          />
          <Chip label={`${selectedDocs.length} selected`} size="small" sx={{ mt: 2 }} />
          {selectionMessage ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {selectionMessage}
            </Alert>
          ) : null}
          {searchError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {searchError}
            </Alert>
          ) : null}
        </Paper>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}
          >
            <Typography variant="h6">Selected Documents</Typography>
            {selectedDocs.length > 0 ? (
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  setSelectedDocs([]);
                  setSelectionMessage(null);
                }}
              >
                Clear all
              </Button>
            ) : null}
          </Box>
          {selectedDocs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No documents selected yet.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {selectedDocs.map((doc, index) => (
                <React.Fragment key={doc.documentId}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {doc.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {doc.documentId}
                      </Typography>
                    </Box>
                    <IconButton
                      aria-label={`Remove ${doc.name}`}
                      onClick={() => handleRemoveDocument(doc.documentId)}
                      size="small"
                    >
                      <Icon>close</Icon>
                    </IconButton>
                  </Box>
                  {index < selectedDocs.length - 1 ? <Divider /> : null}
                </React.Fragment>
              ))}
            </Box>
          )}
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Generate Artifact
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The summary flow will receive only the selected document IDs and the chosen profile.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {GENERATION_ACTIONS.map((action) => {
              const canGenerate = canGenerateAction(action, selectedDocs.length);
              return (
                <Button
                  key={action.profile}
                  disabled={!canGenerate}
                  onClick={() => handleGenerate(action)}
                  variant="contained"
                >
                  {action.label}
                </Button>
              );
            })}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default ManualTranscriptsPage;
